import { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop, {
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { 
  Phone, 
  MessageCircle, 
  Store, 
  Truck, 
  Save, 
  RefreshCw, 
  Check, 
  DollarSign, 
  Crop as CropIcon, 
  Upload, 
  Layout, 
  Image as ImageIcon, 
  Smartphone, 
  Globe, 
  Settings as SettingsIcon,
  Trash2,
  Plus,
  Facebook,
  Instagram,
  Twitter,
  ExternalLink,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuthGuard } from '@/features/auth/components/auth-guard';
import { parseOffers, serializeOffers, type Offer, type OfferDayOfWeek } from '@/features/offers/offers';
import { supabase } from '@/lib/supabase';
import { mergeSettingsCache } from '@/features/settings/hooks/use-settings';
import { uploadBannerImage, uploadCarouselImage, deleteStorageImage } from '@/lib/storage';
import {
  type SupportContact,
  createEmptySupportContact,
  ensureSupportContactDefaults,
  getContactWhatsappTarget,
  getDefaultSupportContact as getDefaultSupportContactFromList,
  parseSupportContacts,
  serializeSupportContacts,
} from '@/features/settings/support-contacts';
import {
  SMS_ENABLED_SETTING_KEY,
  SMS_PROVIDER_API_KEY_SETTING_KEY,
  SMS_PROVIDER_SENDER_ID_SETTING_KEY,
  SMS_PROVIDER_USERNAME_SETTING_KEY,
  SMS_TEMPLATE_DEFINITIONS,
  SMS_TEMPLATE_PLACEHOLDERS,
  SMS_TEST_PHONE_SETTING_KEY,
  type SmsTemplateKey,
} from '@/features/sms/config';

// ---------------------------------------------------------------------------
// Helpers & Types
// ---------------------------------------------------------------------------

function revokeObjectUrl(url: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

interface CarouselItem {
  id: string;
  url: string;
  alt: string;
  title?: string;
  subtitle?: string;
}

interface BusinessSettings {
  business_name: string;
  support_phone: string;
  whatsapp_business_number: string;
  support_numbers: SupportContact[];
  show_prices: string;
  
  // Banner settings
  banner_enabled: boolean;
  banner_url: string;
  banner_alt: string;
  banner_fit: string;
  banner_layout: 'compact' | 'balanced' | 'cinematic';
  
  // Logo settings
  logo_url: string;
  logo_alt: string;
  
  // Promotions / offers (stored in admin_settings)
  offers_timezone: string;
  promotions: string; // JSON string

  // Carousel settings
  carousel_enabled: boolean;
  carousel_autoplay: boolean;
  carousel_interval: number;
  carousel_images: string; // Store as JSON string

  // Hero image settings
  jumbo_image_url: string;
  jumbo_image_enabled: boolean;

  // Social settings
  social_facebook: string;
  social_instagram: string;
  social_twitter: string;
  social_tiktok: string;
  social_show_share_buttons: boolean;

  // SMS Configuration
  sms_enabled: boolean;
  sms_provider_username: string;
  sms_provider_api_key: string;
  sms_provider_sender_id: string;
  sms_test_phone: string;
  
  // SMS Toggles
  sms_event_order_confirmed_enabled: boolean;
  sms_event_new_order_admin_enabled: boolean;
  sms_event_rider_assigned_rider_enabled: boolean;
  sms_event_rider_assigned_customer_enabled: boolean;

  // SMS Templates
  sms_template_order_confirmed_customer: string;
  sms_template_new_order_admin: string;
  sms_template_rider_assigned_rider: string;
  sms_template_rider_assigned_customer: string;
  whatsapp_order_message_template: string;
  // Stock management
  unlimited_stock: boolean;
}

interface SmsLogEntry {
  id: string;
  order_id: string | null;
  event_type: string;
  recipient_type: string;
  phone_number: string;
  message: string;
  status: 'sent' | 'failed';
  response_payload: Record<string, unknown> | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [settings, setSettings] = useState<BusinessSettings>({
    business_name: '',
    support_phone: '',
    whatsapp_business_number: '',
    support_numbers: [],
    show_prices: 'true',
    banner_enabled: true,
    banner_url: '',
    banner_alt: '',
    banner_fit: 'cover',
    banner_layout: 'balanced',
    logo_url: '',
    logo_alt: 'Karebe Wines & Spirits',
    offers_timezone: 'Africa/Nairobi',
    promotions: '[]',
    carousel_enabled: false,
    carousel_autoplay: true,
    carousel_interval: 5000,
    carousel_images: '[]',
    jumbo_image_url: '',
    jumbo_image_enabled: false,
    social_facebook: '',
    social_instagram: '',
    social_twitter: '',
    social_tiktok: '',
    social_show_share_buttons: true,
    sms_enabled: false,
    sms_provider_username: '',
    sms_provider_api_key: '',
    sms_provider_sender_id: '',
    sms_test_phone: '',
    sms_event_order_confirmed_enabled: true,
    sms_event_new_order_admin_enabled: true,
    sms_event_rider_assigned_rider_enabled: true,
    sms_event_rider_assigned_customer_enabled: true,
    sms_template_order_confirmed_customer: 'Hi {{customer_name}}, your order {{order_reference}} has been confirmed.',
    sms_template_new_order_admin: 'New order {{order_reference}} started for {{customer_name}}.',
    sms_template_rider_assigned_rider: 'Hi {{rider_name}}, new delivery for {{order_reference}}.',
    sms_template_rider_assigned_customer: 'Hi {{customer_name}}, rider {{rider_name}} assigned to {{order_reference}}.',
    whatsapp_order_message_template: 'Hello Karebe, I created order {{order_reference}} for {{items_summary}}.',
    unlimited_stock: true,
  });

  const [smsLogs, setSmsLogs] = useState<SmsLogEntry[]>([]);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);
  const [smsActionLoading, setSmsActionLoading] = useState(false);
  const [smsActionMessage, setSmsActionMessage] = useState<string | null>(null);

  // Upload/Crop State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [rawBannerSrc, setRawBannerSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  
  const cropImgRef = useRef<HTMLImageElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);
  const logoFileRef = useRef<HTMLInputElement>(null);
  const carouselFileRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // Data Loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    loadSettings();
    loadSmsLogs();
  }, []);

  const loadSettings = async () => {
    if (!supabase) { setIsLoading(false); return; }
    try {
      const { data, error } = await supabase.from('admin_settings').select('setting_key, setting_value');
      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      data?.forEach((row: { setting_key: string; setting_value: string | null }) => {
        settingsMap[row.setting_key] = row.setting_value || '';
      });

      const supportNumbers = parseSupportContacts(settingsMap.support_contact_numbers, {
        supportPhone: settingsMap.support_phone,
        whatsappBusinessNumber: settingsMap.whatsapp_business_number,
      });
      const defaultSupportContact = getDefaultSupportContactFromList(supportNumbers);

      setSettings(prev => ({
        ...prev,
        business_name: settingsMap.business_name || 'Karebe Wines & Spirits',
        support_numbers: supportNumbers,
        show_prices: settingsMap.show_prices || 'true',
        banner_enabled: settingsMap.banner_enabled !== 'false',
        banner_url: settingsMap.banner_url || '',
        banner_alt: settingsMap.banner_alt || '',
        banner_fit: settingsMap.banner_fit || 'cover',
        banner_layout: (settingsMap.banner_layout as any) || 'balanced',
        logo_url: settingsMap.logo_url || '',
        logo_alt: settingsMap.logo_alt || 'Karebe Wines & Spirits',
        offers_timezone: settingsMap.offers_timezone || 'Africa/Nairobi',
        promotions: settingsMap.promotions || '[]',
        carousel_enabled: settingsMap.carousel_enabled === 'true',
        carousel_autoplay: settingsMap.carousel_autoplay !== 'false',
        carousel_interval: parseInt(settingsMap.carousel_interval || '5000', 10),
        carousel_images: settingsMap.carousel_images || '[]',
        jumbo_image_url: settingsMap.jumbo_image_url || '',
        jumbo_image_enabled: settingsMap.jumbo_image_enabled === 'true',
        social_facebook: settingsMap.social_facebook || '',
        social_instagram: settingsMap.social_instagram || '',
        social_twitter: settingsMap.social_twitter || '',
        social_tiktok: settingsMap.social_tiktok || '',
        social_show_share_buttons: settingsMap.social_show_share_buttons !== 'false',
        sms_enabled: settingsMap[SMS_ENABLED_SETTING_KEY] === 'true',
        sms_provider_username: settingsMap[SMS_PROVIDER_USERNAME_SETTING_KEY] || '',
        sms_provider_api_key: settingsMap[SMS_PROVIDER_API_KEY_SETTING_KEY] || '',
        sms_provider_sender_id: settingsMap[SMS_PROVIDER_SENDER_ID_SETTING_KEY] || '',
        sms_test_phone: settingsMap[SMS_TEST_PHONE_SETTING_KEY] || defaultSupportContact?.phone || '',
        sms_event_order_confirmed_enabled: settingsMap.sms_event_order_confirmed_enabled !== 'false',
        sms_event_new_order_admin_enabled: settingsMap.sms_event_new_order_admin_enabled !== 'false',
        sms_event_rider_assigned_rider_enabled: settingsMap.sms_event_rider_assigned_rider_enabled !== 'false',
        sms_event_rider_assigned_customer_enabled: settingsMap.sms_event_rider_assigned_customer_enabled !== 'false',
        sms_template_order_confirmed_customer: settingsMap.sms_template_order_confirmed_customer || prev.sms_template_order_confirmed_customer,
        sms_template_new_order_admin: settingsMap.sms_template_new_order_admin || prev.sms_template_new_order_admin,
        sms_template_rider_assigned_rider: settingsMap.sms_template_rider_assigned_rider || prev.sms_template_rider_assigned_rider,
        sms_template_rider_assigned_customer: settingsMap.sms_template_rider_assigned_customer || prev.sms_template_rider_assigned_customer,
        whatsapp_order_message_template: settingsMap.whatsapp_order_message_template || prev.whatsapp_order_message_template,
        unlimited_stock: settingsMap.unlimited_stock !== 'false',
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSmsLogs = async () => {
    if (!supabase) return;
    setSmsLogsLoading(true);
    try {
      const { data, error } = await supabase
        .from('sms_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      setSmsLogs((data || []) as SmsLogEntry[]);
    } catch (e) { console.error(e); } finally { setSmsLogsLoading(false); }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const normalizedSupport = parseSupportContacts(serializeSupportContacts(settings.support_numbers));
      const defaultContact = getDefaultSupportContactFromList(normalizedSupport);
      const supportPhone = defaultContact?.phone || '';
      const waNumber = getContactWhatsappTarget(defaultContact);

      const settingsToSave = [
        { key: 'business_name', value: settings.business_name },
        { key: 'support_phone', value: supportPhone },
        { key: 'whatsapp_business_number', value: waNumber },
        { key: 'support_contact_numbers', value: JSON.stringify(normalizedSupport) },
        { key: 'show_prices', value: settings.show_prices },
        { key: 'banner_enabled', value: String(settings.banner_enabled) },
        { key: 'banner_url', value: settings.banner_url },
        { key: 'banner_alt', value: settings.banner_alt },
        { key: 'banner_fit', value: settings.banner_fit },
        { key: 'banner_layout', value: settings.banner_layout },
        { key: 'logo_url', value: settings.logo_url },
        { key: 'logo_alt', value: settings.logo_alt },
        { key: 'offers_timezone', value: settings.offers_timezone },
        { key: 'promotions', value: settings.promotions },
        { key: 'carousel_enabled', value: String(settings.carousel_enabled) },
        { key: 'carousel_autoplay', value: String(settings.carousel_autoplay) },
        { key: 'carousel_interval', value: String(settings.carousel_interval) },
        { key: 'carousel_images', value: settings.carousel_images },
        { key: 'jumbo_image_url', value: settings.jumbo_image_url },
        { key: 'jumbo_image_enabled', value: String(settings.jumbo_image_enabled) },
        { key: 'social_facebook', value: settings.social_facebook },
        { key: 'social_instagram', value: settings.social_instagram },
        { key: 'social_twitter', value: settings.social_twitter },
        { key: 'social_tiktok', value: settings.social_tiktok },
        { key: 'social_show_share_buttons', value: String(settings.social_show_share_buttons) },
        { key: SMS_ENABLED_SETTING_KEY, value: String(settings.sms_enabled) },
        { key: SMS_PROVIDER_USERNAME_SETTING_KEY, value: settings.sms_provider_username.trim() },
        { key: SMS_PROVIDER_API_KEY_SETTING_KEY, value: settings.sms_provider_api_key.trim() },
        { key: SMS_PROVIDER_SENDER_ID_SETTING_KEY, value: settings.sms_provider_sender_id.trim() },
        { key: SMS_TEST_PHONE_SETTING_KEY, value: settings.sms_test_phone.trim() },
        { key: 'sms_event_order_confirmed_enabled', value: String(settings.sms_event_order_confirmed_enabled) },
        { key: 'sms_event_new_order_admin_enabled', value: String(settings.sms_event_new_order_admin_enabled) },
        { key: 'sms_event_rider_assigned_rider_enabled', value: String(settings.sms_event_rider_assigned_rider_enabled) },
        { key: 'sms_event_rider_assigned_customer_enabled', value: String(settings.sms_event_rider_assigned_customer_enabled) },
        { key: 'sms_template_order_confirmed_customer', value: settings.sms_template_order_confirmed_customer },
        { key: 'sms_template_new_order_admin', value: settings.sms_template_new_order_admin },
        { key: 'sms_template_rider_assigned_rider', value: settings.sms_template_rider_assigned_rider },
        { key: 'sms_template_rider_assigned_customer', value: settings.sms_template_rider_assigned_customer },
        { key: 'whatsapp_order_message_template', value: settings.whatsapp_order_message_template },
        { key: 'unlimited_stock', value: String(settings.unlimited_stock) },
      ];

      if (supabase) {
        const now = new Date().toISOString();
        const payload = settingsToSave.map(({ key, value }) => ({
          setting_key: key,
          setting_value: value,
          updated_at: now,
        }));

        const { error } = await (supabase.from('admin_settings') as any).upsert(payload, {
          onConflict: 'setting_key',
        });

        if (error) {
          throw error;
        }
      }

      mergeSettingsCache({
        ...settings,
        support_phone: supportPhone,
        whatsapp_business_number: waNumber,
        support_numbers: normalizedSupport,
        carousel_images: JSON.parse(settings.carousel_images || '[]'),
      } as any);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save failed:', error);
      alert(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Action Handlers
  // ---------------------------------------------------------------------------

  const handleLogoUpload = async (file: File | null) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const url = await uploadBannerImage(file);
      setSettings(p => ({ ...p, logo_url: url }));
    } catch (e) { alert('Logo upload failed'); }
    finally { setIsUploading(false); }
  };

  const handleCarouselFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const url = await uploadCarouselImage(file);
      const current = JSON.parse(settings.carousel_images || '[]');
      const updated = [...current, { id: `slide-${Date.now()}`, url, alt: file.name, title: '', subtitle: '' }];
      setSettings(p => ({ ...p, carousel_images: JSON.stringify(updated) }));
    } catch (e) { alert('Upload failed'); }
    finally { setIsUploading(false); if(carouselFileRef.current) carouselFileRef.current.value = ''; }
  };

  const removeCarouselItem = (id: string) => {
    const current = JSON.parse(settings.carousel_images || '[]');
    const updated = current.filter((i: any) => i.id !== id);
    setSettings(p => ({ ...p, carousel_images: JSON.stringify(updated) }));
  };

  const onBannerImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop({ unit: '%', x: 5, y: 5, width: width > height ? 90 : 80, height: width > height ? 80 : 90 });
  }, []);

  const handleApplyBannerCrop = useCallback(async () => {
    if (!cropImgRef.current) return;
    const img = cropImgRef.current;
    const canvas = document.createElement('canvas');
    const activeCrop = completedCrop?.width && completedCrop?.height ? completedCrop : { x: 0, y: 0, width: img.width, height: img.height };
    canvas.width = activeCrop.width;
    canvas.height = activeCrop.height;
    const ctx = canvas.getContext('2d')!;
    const sx = img.naturalWidth / img.width;
    const sy = img.naturalHeight / img.height;
    ctx.drawImage(img, activeCrop.x * sx, activeCrop.y * sy, activeCrop.width * sx, activeCrop.height * sy, 0, 0, activeCrop.width, activeCrop.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setSettings(p => ({ ...p, banner_url: dataUrl }));
    setRawBannerSrc(null);
  }, [completedCrop]);

  const sendTestSms = async () => {
    if (!supabase) return;
    setSmsActionLoading(true);
    try {
      await supabase.functions.invoke('sms-notifications', { body: { action: 'send_test_sms', phoneNumber: settings.sms_test_phone, message: 'Karebe SMS Working!' }});
      setSmsActionMessage('Test SMS Sent!');
      loadSmsLogs();
    } catch (e) { setSmsActionMessage('Failed'); }
    finally { setSmsActionLoading(false); }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><RefreshCw className="h-8 w-8 animate-spin text-brand-600" /></div>;

  return (
    <AuthGuard requiredRole="admin">
      <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
        
        {/* Sidebar Navigation */}
        <aside className="w-full md:w-64 bg-white border-r md:h-screen sticky top-0 z-20 flex flex-col">
          <div className="p-6 border-b flex items-center gap-2">
            <SettingsIcon className="h-6 w-6 text-brand-600" />
            <h1 className="text-xl font-bold">Admin Console</h1>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {[
              { id: 'general', label: 'General', icon: Store },
              { id: 'branding', label: 'Branding', icon: Layout },
              { id: 'promotions', label: 'Promotions', icon: DollarSign },
              { id: 'messaging', label: 'Messaging', icon: MessageSquare },
              { id: 'social', label: 'Social & Share', icon: Globe },
              { id: 'advanced', label: 'Advanced', icon: Smartphone },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t">
            <Button className="w-full" onClick={saveSettings} disabled={isSaving}>
              {isSaving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {saveSuccess ? 'Changes Saved' : 'Save All Changes'}
            </Button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Active Tab Content */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Business Identity</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Business Name</Label>
                      <Input value={settings.business_name} onChange={e => setSettings({ ...settings, business_name: e.target.value })} />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div>
                        <p className="font-medium">Show Prices</p>
                        <p className="text-sm text-gray-500">Toggle if customers see item prices</p>
                      </div>
                      <Switch checked={settings.show_prices === 'true'} onCheckedChange={c => setSettings({ ...settings, show_prices: String(c) })} />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <p className="font-medium text-green-900">Unlimited Stock Mode</p>
                        <p className="text-sm text-green-700">When on, all items are always available to order regardless of stock quantity. Turn off to enforce tracked stock levels.</p>
                      </div>
                      <Switch checked={settings.unlimited_stock} onCheckedChange={c => setSettings({ ...settings, unlimited_stock: c })} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'branding' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Logo & Banners</CardTitle></CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <Label>Nav Logo</Label>
                        <div className="h-24 w-24 bg-gray-100 rounded-lg border-2 border-dashed flex items-center justify-center relative overflow-hidden group">
                          {settings.logo_url ? <img src={settings.logo_url} className="h-full w-full object-contain" /> : <Plus className="text-gray-400" />}
                          <input type="file" ref={logoFileRef} className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => handleLogoUpload(e.target.files?.[0] || null)} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Site Banner (Crop enabled)</Label>
                        <Button variant="outline" className="w-full" onClick={() => bannerFileRef.current?.click()}>Pick Banner Image</Button>
                        <input type="file" ref={bannerFileRef} hidden onChange={e => { const f = e.target.files?.[0]; if(f) setRawBannerSrc(URL.createObjectURL(f)); }} />
                        {settings.banner_url && <img src={settings.banner_url} className="w-full h-20 object-cover rounded mt-2 border" />}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex justify-between">
                      <CardTitle>Home Carousel Slides</CardTitle>
                      <Switch checked={settings.carousel_enabled} onCheckedChange={c => setSettings({ ...settings, carousel_enabled: c })} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {JSON.parse(settings.carousel_images || '[]').map((item: CarouselItem) => (
                        <div key={item.id} className="relative group border rounded-lg overflow-hidden h-40 bg-gray-50">
                          <img src={item.url} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button size="icon" variant="danger" onClick={() => removeCarouselItem(item.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                      <button 
                        onClick={() => carouselFileRef.current?.click()}
                        className="h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 hover:text-brand-600 hover:border-brand-300 transition-colors"
                      >
                        <Plus className="h-8 w-8 mb-2" />
                        <span>Add Slide</span>
                      </button>
                      <input type="file" ref={carouselFileRef} hidden onChange={handleCarouselFile} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'promotions' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Timed Offers</CardTitle>
                    <CardDescription>
                      Configure “Special Deals & Promotions” shown on the homepage. Use dates/days/times to make them
                      automatically appear/disappear.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label>Offers Timezone</Label>
                      <Input
                        value={settings.offers_timezone}
                        onChange={(e) => setSettings({ ...settings, offers_timezone: e.target.value })}
                        placeholder="Africa/Nairobi"
                      />
                      <p className="text-xs text-gray-500">
                        Used for day-of-week and time windows (e.g. “Whiskey Wednesday”).
                      </p>
                    </div>

                    <Separator />

                    {(() => {
                      const offers = parseOffers(settings.promotions);

                      const setOffers = (next: Offer[]) => {
                        setSettings((p) => ({ ...p, promotions: serializeOffers(next) }));
                      };

                      const dayButtons: Array<{ label: string; value: OfferDayOfWeek }> = [
                        { label: 'Mon', value: 1 },
                        { label: 'Tue', value: 2 },
                        { label: 'Wed', value: 3 },
                        { label: 'Thu', value: 4 },
                        { label: 'Fri', value: 5 },
                        { label: 'Sat', value: 6 },
                        { label: 'Sun', value: 0 },
                      ];

                      const gradients = [
                        'from-amber-500 to-orange-600',
                        'from-green-500 to-emerald-600',
                        'from-amber-700 to-yellow-600',
                        'from-blue-500 to-indigo-600',
                        'from-fuchsia-500 to-pink-600',
                        'from-slate-700 to-slate-900',
                      ];

                      return (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">Offers</p>
                              <p className="text-xs text-gray-500">These are stored in `admin_settings.promotions`.</p>
                            </div>
                            <Button
                              type="button"
                              onClick={() =>
                                setOffers([
                                  ...offers,
                                  {
                                    id: `offer-${Date.now()}`,
                                    title: 'New offer',
                                    description: '',
                                    discount: '',
                                    code: '',
                                    bgColor: gradients[0],
                                    enabled: true,
                                    startsOn: '',
                                    endsOn: '',
                                    daysOfWeek: [],
                                    dailyStartTime: '',
                                    dailyEndTime: '',
                                    link: '',
                                  },
                                ])
                              }
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Offer
                            </Button>
                          </div>

                          {offers.length === 0 ? (
                            <div className="rounded-xl border bg-gray-50 p-6 text-center text-sm text-gray-600">
                              No offers configured yet.
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {offers.map((offer) => (
                                <Card key={offer.id} className="border-gray-200">
                                  <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="min-w-0">
                                        <CardTitle className="text-base truncate">{offer.title}</CardTitle>
                                        <CardDescription className="truncate">{offer.description}</CardDescription>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Switch
                                          checked={offer.enabled !== false}
                                          onCheckedChange={(checked) =>
                                            setOffers(
                                              offers.map((o) => (o.id === offer.id ? { ...o, enabled: checked } : o))
                                            )
                                          }
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => setOffers(offers.filter((o) => o.id !== offer.id))}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </CardHeader>
                                  <CardContent className="space-y-4">
                                    <div className="grid gap-4 md:grid-cols-2">
                                      <div className="space-y-2">
                                        <Label>Title</Label>
                                        <Input
                                          value={offer.title}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, title: e.target.value } : o
                                              )
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Discount Label</Label>
                                        <Input
                                          value={offer.discount || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, discount: e.target.value } : o
                                              )
                                            )
                                          }
                                          placeholder="15%"
                                        />
                                      </div>
                                    </div>

                                    <div className="space-y-2">
                                      <Label>Description</Label>
                                      <Textarea
                                        rows={2}
                                        value={offer.description || ''}
                                        onChange={(e) =>
                                          setOffers(
                                            offers.map((o) =>
                                              o.id === offer.id ? { ...o, description: e.target.value } : o
                                            )
                                          )
                                        }
                                      />
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-3">
                                      <div className="space-y-2">
                                        <Label>Coupon Code</Label>
                                        <Input
                                          value={offer.code || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, code: e.target.value } : o
                                              )
                                            )
                                          }
                                          placeholder="WINE15"
                                        />
                                      </div>
                                      <div className="space-y-2 md:col-span-2">
                                        <Label>Link (optional)</Label>
                                        <Input
                                          value={offer.link || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, link: e.target.value } : o
                                              )
                                            )
                                          }
                                          placeholder="/catalog?category=Wine"
                                        />
                                      </div>
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-3">
                                      <div className="space-y-2">
                                        <Label>Start Date</Label>
                                        <Input
                                          type="date"
                                          value={offer.startsOn || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, startsOn: e.target.value } : o
                                              )
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>End Date</Label>
                                        <Input
                                          type="date"
                                          value={offer.endsOn || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) => (o.id === offer.id ? { ...o, endsOn: e.target.value } : o))
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Gradient</Label>
                                        <select
                                          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                          value={offer.bgColor || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, bgColor: e.target.value } : o
                                              )
                                            )
                                          }
                                        >
                                          {gradients.map((g) => (
                                            <option key={g} value={g}>
                                              {g}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>

                                    <div className="space-y-2">
                                      <Label>Days Of Week (optional)</Label>
                                      <div className="flex flex-wrap gap-2">
                                        {dayButtons.map((d) => {
                                          const isOn = (offer.daysOfWeek || []).includes(d.value);
                                          return (
                                            <button
                                              key={d.label}
                                              type="button"
                                              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                                isOn
                                                  ? 'bg-brand-600 text-white border-brand-600'
                                                  : 'bg-white text-gray-700 border-gray-200 hover:border-brand-300'
                                              }`}
                                              onClick={() => {
                                                const current = offer.daysOfWeek || [];
                                                const next = isOn
                                                  ? current.filter((x) => x !== d.value)
                                                  : [...current, d.value];
                                                setOffers(
                                                  offers.map((o) => (o.id === offer.id ? { ...o, daysOfWeek: next } : o))
                                                );
                                              }}
                                            >
                                              {d.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        Leave empty to show every day.
                                      </p>
                                    </div>

                                    <div className="grid gap-4 md:grid-cols-2">
                                      <div className="space-y-2">
                                        <Label>Daily Start Time (optional)</Label>
                                        <Input
                                          type="time"
                                          value={offer.dailyStartTime || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, dailyStartTime: e.target.value } : o
                                              )
                                            )
                                          }
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Daily End Time (optional)</Label>
                                        <Input
                                          type="time"
                                          value={offer.dailyEndTime || ''}
                                          onChange={(e) =>
                                            setOffers(
                                              offers.map((o) =>
                                                o.id === offer.id ? { ...o, dailyEndTime: e.target.value } : o
                                              )
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'messaging' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Support Numbers</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {settings.support_numbers.map((contact) => (
                      <div key={contact.id} className="border p-4 rounded-lg bg-gray-50 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <Label className="text-sm">Default</Label>
                            <Switch
                              checked={Boolean(contact.isDefault)}
                              onCheckedChange={(checked) => {
                                if (!checked) return;
                                setSettings((p) => ({
                                  ...p,
                                  support_numbers: ensureSupportContactDefaults(
                                    p.support_numbers.map((c) => ({
                                      ...c,
                                      isDefault: c.id === contact.id,
                                    }))
                                  ),
                                }));
                              }}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setSettings((p) => {
                                const next = p.support_numbers.filter((c) => c.id !== contact.id);
                                return {
                                  ...p,
                                  support_numbers: ensureSupportContactDefaults(next),
                                };
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                          <div className="space-y-2">
                            <Label>Label</Label>
                            <Input
                              value={contact.label}
                              onChange={(e) =>
                                setSettings((p) => ({
                                  ...p,
                                  support_numbers: p.support_numbers.map((c) =>
                                    c.id === contact.id ? { ...c, label: e.target.value } : c
                                  ),
                                }))
                              }
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Phone (Call/SMS)</Label>
                            <Input
                              value={contact.phone}
                              onChange={(e) =>
                                setSettings((p) => ({
                                  ...p,
                                  support_numbers: p.support_numbers.map((c) =>
                                    c.id === contact.id ? { ...c, phone: e.target.value } : c
                                  ),
                                }))
                              }
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>WhatsApp (optional)</Label>
                            <Input
                              placeholder="Leave blank to use Phone"
                              value={contact.whatsappPhone || ''}
                              onChange={(e) =>
                                setSettings((p) => ({
                                  ...p,
                                  support_numbers: p.support_numbers.map((c) =>
                                    c.id === contact.id
                                      ? { ...c, whatsappPhone: e.target.value }
                                      : c
                                  ),
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        setSettings((p) => ({
                          ...p,
                          support_numbers: ensureSupportContactDefaults([
                            ...p.support_numbers,
                            createEmptySupportContact(p.support_numbers.length),
                          ]),
                        }))
                      }
                    >
                      Add Contact
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle>WhatsApp Checkout Template</CardTitle></CardHeader>
                  <CardContent>
                    <Textarea rows={4} value={settings.whatsapp_order_message_template} onChange={e => setSettings({ ...settings, whatsapp_order_message_template: e.target.value })} />
                    <p className="text-xs text-gray-500 mt-2">Placeholders: {"{{order_reference}}, {{items_summary}}, {{amount_summary}}, {{customer_name}}"}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'social' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Social Media Presence</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Facebook className="h-4 w-4" /> Facebook</Label>
                        <Input placeholder="facebook.com/karebewines" value={settings.social_facebook} onChange={e => setSettings({ ...settings, social_facebook: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Instagram className="h-4 w-4" /> Instagram</Label>
                        <Input placeholder="@karebewines" value={settings.social_instagram} onChange={e => setSettings({ ...settings, social_instagram: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><Twitter className="h-4 w-4" /> Twitter / X</Label>
                        <Input placeholder="@karebewines" value={settings.social_twitter} onChange={e => setSettings({ ...settings, social_twitter: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2"><ImageIcon className="h-4 w-4" /> TikTok</Label>
                        <Input placeholder="@karebewines" value={settings.social_tiktok} onChange={e => setSettings({ ...settings, social_tiktok: e.target.value })} />
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Direct Share Buttons</p>
                        <p className="text-sm text-gray-500">Show social share icons on product pages</p>
                      </div>
                      <Switch checked={settings.social_show_share_buttons} onCheckedChange={c => setSettings({ ...settings, social_show_share_buttons: c })} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <Card className="border-red-100">
                  <CardHeader className="bg-red-50/50"><CardTitle className="text-red-900">SMS Engine (Global Toggles)</CardTitle></CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
                      <div>
                        <p className="font-bold text-red-900">Global SMS Master Switch</p>
                        <p className="text-sm text-red-700">Completely disable all SMS automated messaging</p>
                      </div>
                      <Switch checked={settings.sms_enabled} onCheckedChange={c => setSettings({ ...settings, sms_enabled: c })} />
                    </div>
                    
                    <div className="grid gap-3">
                      <Label className="text-gray-500 uppercase text-[10px] font-bold">Execution Hooks</Label>
                      {[
                        { id: 'sms_event_order_confirmed_enabled', label: 'Order Confirmed (To Customer)', desc: 'Sent when order is marked as confirmed' },
                        { id: 'sms_event_new_order_admin_enabled', label: 'New Order Alert (To Admin)', desc: 'Sent when any order is created' },
                        { id: 'sms_event_rider_assigned_rider_enabled', label: 'Assignment Alert (To Rider)', desc: 'Disable this to stop rider pings' },
                        { id: 'sms_event_rider_assigned_customer_enabled', label: 'Assignment Update (To Customer)', desc: 'Sent when a rider is picked' },
                      ].map(ev => (
                        <div key={ev.id} className="flex items-center justify-between p-3 border rounded">
                          <div className="max-w-[80%]">
                            <p className="text-sm font-medium">{ev.label}</p>
                            <p className="text-[11px] text-gray-400">{ev.desc}</p>
                          </div>
                          <Switch checked={(settings as any)[ev.id]} onCheckedChange={c => setSettings({ ...settings, [ev.id]: c })} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle>Credentials & Provider</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>AT Username</Label><Input value={settings.sms_provider_username} onChange={e => setSettings({...settings, sms_provider_username: e.target.value})} /></div>
                      <div className="space-y-2"><Label>AT API Key</Label><Input type="password" value={settings.sms_provider_api_key} onChange={e => setSettings({...settings, sms_provider_api_key: e.target.value})} /></div>
                      <div className="space-y-2"><Label>Sender ID</Label><Input value={settings.sms_provider_sender_id} onChange={e => setSettings({...settings, sms_provider_sender_id: e.target.value})} /></div>
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="Test Phone" value={settings.sms_test_phone} onChange={e => setSettings({...settings, sms_test_phone: e.target.value})} />
                      <Button variant="outline" onClick={sendTestSms} disabled={smsActionLoading}>Send Test</Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-sm">Activity Logs</CardTitle></CardHeader>
                  <CardContent>
                    <div className="h-[200px] overflow-y-auto border rounded text-[10px] font-mono bg-black text-green-400 p-2">
                      {smsLogs.length === 0 ? <p className="opacity-50"># Waiting for logs...</p> : smsLogs.map(l => (
                        <div key={l.id} className="mb-1">
                          [{new Date(l.created_at).toLocaleTimeString()}] {l.status.toUpperCase()} {'->'} {l.phone_number}: {l.message.slice(0, 30)}...
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

          </div>
        </main>
      </div>

      {/* Crop Modal */}
      {rawBannerSrc && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full p-6 space-y-6">
            <h3 className="text-xl font-bold">Crop Banner</h3>
            <div className="max-h-[60vh] overflow-auto">
              <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)} aspect={16/6}>
                <img ref={cropImgRef} src={rawBannerSrc} onLoad={onBannerImageLoad} className="max-w-full" />
              </ReactCrop>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRawBannerSrc(null)}>Cancel</Button>
              <Button onClick={handleApplyBannerCrop}>Apply & Save</Button>
            </div>
          </div>
        </div>
      )}
    </AuthGuard>
  );
}
